"use client";

import React, { useState, useMemo, useEffect } from 'react';
import { useTheme } from '@/components/ThemeContext';
import { 
  Building2, User, AlertTriangle, TrendingUp, TrendingDown, 
  Clock, X, Check, Mail, Phone, ChevronRight, FileText, BellRing, ChevronDown, CheckCircle2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// --- Types & Mock Data ---

type Bucket = 'current' | 'd0_30' | 'd31_60' | 'd61_90' | 'd90_plus' | 'lost';
const BUCKET_COLORS = {
  current: 'bg-slate-400',
  d0_30: 'bg-emerald-500',
  d31_60: 'bg-amber-500',
  d61_90: 'bg-orange-500',
  d90_plus: 'bg-rose-500',
  lost: 'bg-slate-800'
};

const BUCKET_TEXT = {
  current: '정상',
  d0_30: '0-30일',
  d31_60: '31-60일',
  d61_90: '61-90일',
  d90_plus: '90일 초과',
  lost: '취소/기타'
};

interface ProjectAR {
  id: string;
  name: string;
  code: string;
  type: string;
  vendor: { name: string; abbr: string };
  pm: { name: string; abbr: string };
  totalAmount: number;
  unpaid: number;
  invoiceIssuedAt: string;
  dueDate: string;
  paidAt: string | null;
  status: 'active' | 'lost' | 'cancelled';
  unnotified: boolean;
  pending: boolean;
  bucket?: string;
  daysOverdue?: number;
}

// 1년치 더미 생성
const todayDate = new Date();
const mockData: ProjectAR[] = Array.from({ length: 45 }).map((_, i) => {
  const isLost = i === 12 || i === 25;
  const daysDiff = Math.floor(Math.random() * 200) - 50; // -50 to 150 days ago
  const dueDateObj = new Date(todayDate);
  dueDateObj.setDate(todayDate.getDate() - daysDiff);
  
  const total = Math.floor(Math.random() * 150 + 20) * 1000000;
  // Some are fully paid
  const isPaid = Math.random() > 0.6 && daysDiff > 30;
  const unpaid = isPaid ? 0 : (Math.random() > 0.4 ? total : Math.floor(total * 0.3));
  
  return {
    id: `PRJ-${String(2400 + i).padStart(4, '0')}`,
    name: ['순천시 신청사', '제부리풀빌라', '이노랩 오피스', '강남 플래그십', '판교 R&D센터', '양평 전원주택'][i % 6] + ` ${i + 1}차`,
    code: `CW-${202400 + i}`,
    type: ['관공서', '상업', '오피스', '주거'][i % 4],
    vendor: {
      name: ['이노랩건축', '정운건축', '아크바디', '서진건설', '현대엔지니어링'][i % 5],
      abbr: ['IN', 'JU', 'AB', 'SJ', 'HE'][i % 5]
    },
    pm: { name: ['김재경', '이현석', '박민우', '최지원'][i % 4], abbr: ['JK', 'HS', 'MW', 'JW'][i % 4] },
    totalAmount: total,
    unpaid: isLost ? 0 : unpaid,
    invoiceIssuedAt: new Date(dueDateObj.getTime() - 14 * 86400000).toISOString().split('T')[0],
    dueDate: dueDateObj.toISOString().split('T')[0],
    paidAt: isPaid ? new Date(dueDateObj.getTime() + 5 * 86400000).toISOString().split('T')[0] : null,
    status: isLost ? 'lost' : 'active',
    unnotified: unpaid > 0 && Math.random() > 0.7,
    pending: unpaid > 0 && Math.random() > 0.8
  };
});

// --- Helper Functions ---

function getDaysOverdue(dueDate: string, paidAt: string | null): number {
  if (paidAt) return 0;
  const diffTime = todayDate.getTime() - new Date(dueDate).getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
}

function getBucket(daysOverdue: number, status: string): Bucket {
  if (status === 'lost' || status === 'cancelled') return 'lost';
  if (daysOverdue === 0) return 'current';
  if (daysOverdue <= 30) return 'd0_30';
  if (daysOverdue <= 60) return 'd31_60';
  if (daysOverdue <= 90) return 'd61_90';
  return 'd90_plus';
}

function formatKoreanAbbr(num: number): string {
  if (num >= 100000000) return `${(num / 100000000).toFixed(1)}억`;
  if (num >= 10000000) return `${(num / 10000000).toFixed(1)}천만`;
  return num.toLocaleString();
}

function formatAmount(num: number, useAbbr: boolean): string {
  return useAbbr ? formatKoreanAbbr(num) : num.toLocaleString();
}

export default function ProjectsInsightTracker({ onProjectClick }: { onProjectClick?: (p: any) => void }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [useAbbr, setUseAbbr] = useState(false);
  const [filterView, setFilterView] = useState<'all'|'overdue90'|'overdue60'|'thisweek'|'unnotified'|'pending'|'lost'>('all');
  const [selectedRow, setSelectedRow] = useState<ProjectAR | null>(null);
  const [showAiBanner, setShowAiBanner] = useState(true);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Data processing
  const processedData = useMemo(() => {
    return mockData.map(row => {
      const daysOverdue = getDaysOverdue(row.dueDate, row.paidAt);
      return {
        ...row,
        daysOverdue,
        bucket: getBucket(daysOverdue, row.status)
      };
    });
  }, []);

  const activeData = useMemo(() => processedData.filter(d => d.status === 'active'), [processedData]);

  // KPIs
  const totalUnpaid = activeData.reduce((sum, d) => sum + d.unpaid, 0);
  const totalAmount = activeData.reduce((sum, d) => sum + d.totalAmount, 0);
  const collectionRate = totalAmount === 0 ? 0 : ((totalAmount - totalUnpaid) / totalAmount) * 100;
  
  const overdueItems = activeData.filter(d => d.daysOverdue > 0);
  const avgDSO = overdueItems.length === 0 ? 0 : Math.round(overdueItems.reduce((sum, d) => sum + d.daysOverdue, 0) / overdueItems.length);
  
  const thisWeekTarget = activeData.filter(d => d.dueDate >= new Date(todayDate.getTime() - 7*86400000).toISOString() && d.dueDate <= new Date(todayDate.getTime() + 7*86400000).toISOString());
  const thisWeekUnpaidSum = thisWeekTarget.reduce((sum, d) => sum + d.unpaid, 0);

  // Aging Grouping
  const agingGroups = {
    current: activeData.filter(d => d.bucket === 'current').reduce((s,d)=>s+d.unpaid, 0),
    d0_30: activeData.filter(d => d.bucket === 'd0_30').reduce((s,d)=>s+d.unpaid, 0),
    d31_60: activeData.filter(d => d.bucket === 'd31_60').reduce((s,d)=>s+d.unpaid, 0),
    d61_90: activeData.filter(d => d.bucket === 'd61_90').reduce((s,d)=>s+d.unpaid, 0),
    d90_plus: activeData.filter(d => d.bucket === 'd90_plus').reduce((s,d)=>s+d.unpaid, 0),
  };

  // Vendor High Risk Top 10
  const vendorStats = useMemo(() => {
    const raw: Record<string, any> = {};
    activeData.forEach(d => {
      if (!raw[d.vendor.name]) raw[d.vendor.name] = { id: d.vendor.name, unpaid: 0, d0_30:0, d31_60:0, d61_90:0, d90_plus: 0, count: 0, sumDays: 0, overdueCount: 0 };
      if (d.unpaid > 0) {
        raw[d.vendor.name].unpaid += d.unpaid;
        raw[d.vendor.name].count += 1;
        if (d.bucket !== 'current') {
           raw[d.vendor.name][d.bucket] += d.unpaid;
        }
        if (d.daysOverdue > 0) {
          raw[d.vendor.name].sumDays += d.daysOverdue;
          raw[d.vendor.name].overdueCount += 1;
        }
      }
    });
    return Object.values(raw).filter(v => v.unpaid > 0).sort((a,b) => b.unpaid - a.unpaid).slice(0, 10);
  }, [activeData]);

  // Filtering Table
  const filteredTable = useMemo(() => {
    let result = processedData;
    if (filterView === 'all') result = result.filter(d => d.status === 'active' && d.unpaid > 0);
    else if (filterView === 'lost') result = result.filter(d => d.status === 'lost' || d.status === 'cancelled');
    else {
      result = result.filter(d => d.status === 'active' && d.unpaid > 0);
      if (filterView === 'overdue90') result = result.filter(d => d.bucket === 'd90_plus');
      if (filterView === 'overdue60') result = result.filter(d => d.bucket === 'd61_90' || d.bucket === 'd90_plus');
      if (filterView === 'thisweek') {
        const nextWeek = new Date(todayDate.getTime() + 7 * 86400000).toISOString().split('T')[0];
        const lastWeek = new Date(todayDate.getTime() - 7 * 86400000).toISOString().split('T')[0];
        result = result.filter(d => d.dueDate >= lastWeek && d.dueDate <= nextWeek);
      }
      if (filterView === 'unnotified') result = result.filter(d => d.unnotified);
      if (filterView === 'pending') result = result.filter(d => d.pending);
    }

    return result.sort((a, b) => {
      if (b.daysOverdue !== a.daysOverdue) return b.daysOverdue - a.daysOverdue;
      return b.unpaid - a.unpaid;
    });
  }, [processedData, filterView]);

  if (!mounted) return null;

  return (
    <div className={`p-6 lg:p-8 space-y-8 transition-colors min-h-screen ${isDark ? 'text-slate-200' : 'text-slate-900'}`}>
      
      {/* 0. Header with Abbr Toggle */}
      <div className="flex justify-between items-end">
        <div>
          <h2 className="text-lg font-bold">미수금 종합 관제 (AR Tracker)</h2>
          <p className="text-sm font-medium text-slate-500 mt-1">프로젝트 대금 회수율 및 위험 연체 구간 집중 모니터링</p>
        </div>
        <div className="flex items-center gap-2 text-sm bg-slate-100 dark:bg-slate-800/80 p-1 rounded-lg border border-slate-200 dark:border-white/10">
          <button onClick={() => setUseAbbr(false)} className={`px-3 py-1.5 rounded-md font-medium transition-all ${!useAbbr ? 'bg-white dark:bg-slate-700 shadow-sm text-slate-800 dark:text-white' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}>1원 단위</button>
          <button onClick={() => setUseAbbr(true)} className={`px-3 py-1.5 rounded-md font-medium transition-all ${useAbbr ? 'bg-white dark:bg-slate-700 shadow-sm text-slate-800 dark:text-white' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}>축약 단위</button>
        </div>
      </div>

      {/* 1. AI Anomaly Warning Banner (Dismissable) */}
      <AnimatePresence>
        {showAiBanner && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className={`rounded-xl border flex items-start gap-4 p-4 shadow-sm relative overflow-hidden mix-blend-multiply dark:mix-blend-normal ${isDark ? 'bg-amber-500/10 border-amber-500/20' : 'bg-amber-50 border-amber-200'}`}>
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-bold text-amber-800 dark:text-amber-300">AI 연체 징후 자동 감지</h4>
              <p className="text-[13px] text-amber-700/80 dark:text-amber-400/80 mt-1">이노랩건축은 최근 3건 연속 90일+ 연체 구간에 진입했습니다. 현재 진행 중인 신규 계약의 대금결제조건(Payment Terms) 재검토를 권고합니다.</p>
            </div>
            <button onClick={() => setShowAiBanner(false)} className="text-amber-600/50 hover:text-amber-600 dark:text-amber-500/50 dark:hover:text-amber-400 p-1">
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 2. KPI Hero Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <div className="rounded-2xl border border-slate-200/70 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] dark:border-white/[0.06] dark:bg-slate-900/60 p-6 flex flex-col justify-between group">
          <div>
            <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 flex items-center justify-between mb-3">
              <span>총 미수금</span>
              <span className="px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400">+8.2% MoM ▲</span>
            </div>
            <div className="text-3xl lg:text-4xl font-semibold font-mono tabular-nums text-slate-900 dark:text-white">{formatAmount(totalUnpaid, useAbbr)}</div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-2 font-medium">연체 90일+ 40.1% 잔존</div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200/70 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] dark:border-white/[0.06] dark:bg-slate-900/60 p-6 flex flex-col justify-between">
          <div>
            <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 flex items-center justify-between mb-3">
              <span>총 회수율</span>
              <span className="px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-400">-3.1%p MoM ▼</span>
            </div>
            <div className="text-3xl lg:text-4xl font-semibold font-mono tabular-nums text-slate-900 dark:text-white">{collectionRate.toFixed(1)}%</div>
            <div className="flex items-center gap-2 mt-3">
              <div className="h-1.5 flex-1 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-emerald-500 via-amber-500 to-rose-500 rounded-full" style={{ width: `${collectionRate}%` }}></div>
              </div>
              <span className="text-[10px] font-medium text-slate-400">목표 75%</span>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200/70 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] dark:border-white/[0.06] dark:bg-slate-900/60 p-6 flex flex-col justify-between">
          <div>
            <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 flex items-center justify-between mb-3">
              <span>평균 연체일 (DSO)</span>
              <span className="px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-400">+12일 MoM ▲</span>
            </div>
            <div className="text-3xl lg:text-4xl font-semibold font-mono tabular-nums text-slate-900 dark:text-white flex items-end gap-1">
              {avgDSO}<span className="text-lg font-medium text-slate-400 mb-1">일</span>
            </div>
            <div className="text-xs text-rose-600 dark:text-rose-400 mt-2 font-medium flex items-center gap-1"><AlertTriangle className="w-3 h-3"/> 업계 평균 35일 초과 (위험)</div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200/70 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] dark:border-white/[0.06] dark:bg-slate-900/60 p-6 flex flex-col justify-between cursor-pointer hover:border-blue-500/50 transition-colors" onClick={() => setFilterView('thisweek')}>
          <div>
            <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 flex items-center justify-between mb-3">
              <span>이번 주 회수 목표</span>
              <span className="px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400 text-right w-max h-max">Action</span>
            </div>
            <div className="text-3xl lg:text-4xl font-semibold font-mono tabular-nums text-blue-600 dark:text-blue-400">{formatAmount(thisWeekUnpaidSum, true)}</div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-2 font-medium">총 {thisWeekTarget.length}건 진행 · 2건 대기 <span className="cursor-pointer text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300" onClick={(e) => { e.stopPropagation(); setFilterView('thisweek'); document.getElementById('ar-table-view')?.scrollIntoView({behavior: 'smooth'}); }}>(확인하기 ➔)</span></div>
          </div>
        </div>
      </div>

      {/* 3. Aging Distribution & Top 10 */}
      <div className="grid xl:grid-cols-[1.4fr_1fr] gap-5">
        {/* Aging Chart */}
        <div className="rounded-2xl border border-slate-200/70 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] dark:border-white/[0.06] dark:bg-slate-900/60 p-6">
          <h2 className="text-sm font-semibold mb-6 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-violet-500"/> 미수금 연령 분석 (Aging Bucket)</h2>
          
          <div className="flex h-5 rounded-full overflow-hidden mb-6">
            <div style={{ width: `${(agingGroups.current/totalUnpaid)*100}%` }} className="bg-slate-400 transition-all hover:opacity-80" />
            <div style={{ width: `${(agingGroups.d0_30/totalUnpaid)*100}%` }} className="bg-emerald-500 transition-all hover:opacity-80" />
            <div style={{ width: `${(agingGroups.d31_60/totalUnpaid)*100}%` }} className="bg-amber-500 transition-all hover:opacity-80" />
            <div style={{ width: `${(agingGroups.d61_90/totalUnpaid)*100}%` }} className="bg-orange-500 transition-all hover:opacity-80" />
            <div style={{ width: `${(agingGroups.d90_plus/totalUnpaid)*100}%` }} className="bg-rose-500 transition-all hover:opacity-80" />
          </div>

          <table className="w-full text-sm">
            <tbody className="divide-y divide-slate-100 dark:divide-white/[0.04]">
              {[
                { k: 'd0_30', n: BUCKET_TEXT['d0_30'], c: BUCKET_COLORS['d0_30'], v: agingGroups.d0_30 },
                { k: 'd31_60', n: BUCKET_TEXT['d31_60'], c: BUCKET_COLORS['d31_60'], v: agingGroups.d31_60 },
                { k: 'd61_90', n: BUCKET_TEXT['d61_90'], c: BUCKET_COLORS['d61_90'], v: agingGroups.d61_90 },
                { k: 'd90_plus', n: BUCKET_TEXT['d90_plus'], c: BUCKET_COLORS['d90_plus'], v: agingGroups.d90_plus, isWarn: true },
              ].map(b => (
                <tr key={b.k} className="group cursor-pointer hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors" onClick={() => setFilterView(b.k === 'd90_plus' ? 'overdue90' : (b.k === 'd61_90' ? 'overdue60' : 'all'))}>
                  <td className="py-3 flex items-center gap-3">
                    <span className={`w-2 h-2 rounded-full ${b.c}`}></span>
                    <span className={`font-semibold ${b.isWarn ? 'text-rose-600 dark:text-rose-400' : 'text-slate-700 dark:text-slate-300'}`}>{b.n}</span>
                    {b.isWarn && <span className="px-1.5 py-0.5 bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-400 text-[10px] rounded animate-pulse">위험군</span>}
                  </td>
                  <td className="py-3 text-right">
                    <span className="font-mono tabular-nums font-medium text-slate-800 dark:text-slate-200">{formatAmount(b.v, useAbbr)}</span>
                  </td>
                  <td className="py-3 text-right">
                    <span className="font-mono tabular-nums text-slate-500">{(b.v/totalUnpaid*100).toFixed(1)}%</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Vendor Top Offender */}
        <div className="rounded-2xl border border-slate-200/70 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] dark:border-white/[0.06] dark:bg-slate-900/60 p-6 overflow-hidden flex flex-col h-full max-h-[440px]">
          <h2 className="text-sm font-semibold mb-4 flex items-center gap-2"><Building2 className="w-4 h-4 text-emerald-500"/> 발주처별 미수금 Top 10 집중도</h2>
          <div className="flex-1 overflow-y-auto pr-2 space-y-4">
            {vendorStats.map((v, i) => (
              <div key={i} className="flex gap-4 items-center">
                <div className="w-8 h-8 shrink-0 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-xs font-semibold text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-white/10">{v.id.substring(0,2)}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-end mb-1">
                    <div className="text-[13px] font-semibold text-slate-900 dark:text-slate-200 truncate pr-2">{v.id}</div>
                    <div className="text-[13px] font-mono tabular-nums font-semibold text-rose-600 dark:text-rose-400 shrink-0">{formatAmount(v.unpaid, true)}</div>
                  </div>
                  {/* Mini-bar */}
                  <div className="h-1.5 flex bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden w-full mb-1">
                    {v.d0_30 > 0 && <div className="bg-emerald-500" style={{ width: `${v.d0_30/v.unpaid*100}%` }} />}
                    {v.d31_60 > 0 && <div className="bg-amber-500" style={{ width: `${v.d31_60/v.unpaid*100}%` }} />}
                    {v.d61_90 > 0 && <div className="bg-orange-500" style={{ width: `${v.d61_90/v.unpaid*100}%` }} />}
                    {v.d90_plus > 0 && <div className="bg-rose-500" style={{ width: `${v.d90_plus/v.unpaid*100}%` }} />}
                  </div>
                  <div className="text-[11px] text-slate-400 dark:text-slate-500 font-medium">
                    {v.count}개 현장 · 평균 연체 {Math.round(v.sumDays/v.overdueCount || 0)}일
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 4. Watchlist 3 columns */}
      <div className="grid xl:grid-cols-3 gap-5">
        <WatchlistCard 
          title="🔴 High Risk (90일+ 최장기)"
          items={activeData.filter(d => d.bucket === 'd90_plus').sort((a,b)=>b.daysOverdue - a.daysOverdue).slice(0,4)}
          icon={<AlertTriangle className="w-4 h-4 text-rose-500"/>}
          type="risk"
          onProjectClick={onProjectClick}
          onViewAll={() => { setFilterView('overdue90'); document.getElementById('ar-table-view')?.scrollIntoView({behavior: 'smooth'}); }}
        />
        <WatchlistCard 
          title="🟡 This Week (이번 주 만기)"
          items={thisWeekTarget.slice(0,4)}
          icon={<Clock className="w-4 h-4 text-amber-500"/>}
          type="due"
          onProjectClick={onProjectClick}
          onViewAll={() => { setFilterView('thisweek'); document.getElementById('ar-table-view')?.scrollIntoView({behavior: 'smooth'}); }}
        />
        <div className="rounded-2xl border border-slate-200/70 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] dark:border-white/[0.06] dark:bg-slate-900/60 p-5 flex flex-col">
          <h3 className="text-sm font-semibold mb-4 flex items-center gap-2"><BellRing className="w-4 h-4 text-blue-500"/> 최근 7일 활동내역</h3>
          <div className="flex-1 space-y-4">
            <div className="flex gap-3 text-[13px]"><div className="w-2 h-2 rounded-full bg-emerald-500 mt-1.5 shrink-0"/> <div><b>2건 입금 완료 확인</b> <span className="text-slate-400">(+125M)</span></div></div>
            <div className="flex gap-3 text-[13px]"><div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 shrink-0"/> <div><b>순천 신청사 담당자 통화</b> <span className="text-slate-400">- 다음 주 송금 약속</span></div></div>
            <div className="flex gap-3 text-[13px]"><div className="w-2 h-2 rounded-full bg-amber-500 mt-1.5 shrink-0"/> <div><b>5건 AI 정중한 독촉메일 발송 완료</b></div></div>
            <div className="flex gap-3 text-[13px]"><div className="w-2 h-2 rounded-full bg-rose-500 mt-1.5 shrink-0"/> <div><b>신규 장기미수 1건(90+) 편입</b></div></div>
          </div>
          <div onClick={() => document.getElementById('ar-table-view')?.scrollIntoView({behavior: 'smooth'})} className="mt-4 pt-4 border-t border-slate-100 dark:border-white/[0.04] text-xs font-semibold text-blue-600 dark:text-blue-400 cursor-pointer text-center hover:text-blue-700">전체 타임라인 보기 →</div>
        </div>
      </div>

      {/* 5. Main Table UI with Presets */}
      <div id="ar-table-view" className="scroll-mt-8">
        {/* Preset Tabs */}
        <div className="flex flex-wrap gap-2 mb-4">
          {[
            { id: 'all', n: '전체 (미수/연체)' },
            { id: 'overdue90', n: '🔴 90일+ 위험' },
            { id: 'overdue60', n: '🟠 60일+ 경고' },
            { id: 'thisweek', n: '🟡 이번주 만기' },
            { id: 'unnotified', n: '📨 독촉 미발송' },
            { id: 'pending', n: '✅ 입금 대기' },
            { id: 'lost', n: '🚫 Lost/Cancel' },
          ].map(t => (
            <button key={t.id} onClick={() => setFilterView(t.id as any)} 
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${filterView === t.id ? 'bg-slate-800 text-white dark:bg-white dark:text-slate-900 shadow-md' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 dark:bg-slate-800/60 dark:text-slate-300 dark:border-white/10 dark:hover:bg-slate-700'}`}>
              {t.n} {filterView === t.id && <span className="ml-1 opacity-60">({filteredTable.length})</span>}
            </button>
          ))}
        </div>

        <div className="rounded-2xl border border-slate-200/70 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] dark:border-white/[0.06] dark:bg-slate-900/60 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[1000px]">
              <thead className="bg-slate-50 dark:bg-white/[0.02] border-b border-slate-200/70 dark:border-white/[0.06]">
                <tr>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">프로젝트</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">발주처</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 text-right">계약금액</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 text-right">미수금</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Aging</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">회수 진척</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">만기일</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">PM</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 text-center">액션</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-white/[0.04]">
                {filteredTable.length === 0 ? (
                  <tr><td colSpan={9} className="py-12 text-center text-sm text-slate-500 font-medium">해당 조건에 맞는 프로젝트가 없습니다.</td></tr>
                ) : filteredTable.map(d => {
                  const paid = d.totalAmount - d.unpaid;
                  const progress = (paid / d.totalAmount) * 100;
                  return (
                    <tr key={d.id} title="클릭하여 상세 서랍 열기" onClick={() => onProjectClick?.(d)} className="cursor-pointer transition-colors hover:bg-slate-50/70 dark:hover:bg-white/[0.02]">
                      <td className="px-5 py-3 text-sm">
                        <div className="font-semibold text-slate-800 dark:text-slate-200">{d.name}</div>
                        <div className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">{d.code} · {d.type}</div>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-slate-100 dark:bg-slate-800 text-[9px] font-bold flex items-center justify-center text-slate-500">{d.vendor.abbr}</div>
                          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{d.vendor.name}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <div className="text-sm font-mono tabular-nums text-slate-500 dark:text-slate-400">{d.status === 'lost' ? '-' : formatAmount(d.totalAmount, useAbbr)}</div>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <div className={`text-sm font-mono tabular-nums font-bold ${d.unpaid > 0 && d.status !== 'lost' ? 'text-rose-600 dark:text-rose-400 drop-shadow-[0_0_12px_rgba(244,63,94,0.18)]' : 'text-slate-400'}`}>
                          {d.status === 'lost' ? '-' : (d.unpaid === 0 ? '완납' : formatAmount(d.unpaid, useAbbr))}
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        {d.unpaid > 0 && d.status !== 'lost' ? (
                          <div className="flex items-center gap-2">
                             <span className={`w-2 h-2 rounded-full ${BUCKET_COLORS[d.bucket]}`} />
                             <span className={`text-[11px] font-mono font-bold ${d.bucket === 'd90_plus' ? 'text-rose-600 dark:text-rose-400' : 'text-slate-600 dark:text-slate-400'}`}>D+{d.daysOverdue}</span>
                          </div>
                        ) : <span className="text-slate-400 text-xs">-</span>}
                      </td>
                      <td className="px-5 py-3">
                        {d.status === 'lost' ? <span className="text-xs text-slate-400">취소</span> : (
                          <div className="flex items-center gap-2 w-24">
                            <div className="h-1.5 flex-1 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                              <div className="h-full bg-blue-500" style={{ width: `${progress}%` }}/>
                            </div>
                            <span className="text-[10px] font-mono text-slate-400">{Math.round(progress)}%</span>
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-3">
                         <div className={`text-xs font-mono font-semibold tabular-nums ${(d.dueDate < new Date().toISOString().split('T')[0] && d.unpaid > 0 && d.status!=='lost') ? 'text-rose-600 dark:text-rose-400' : 'text-slate-600 dark:text-slate-400'}`}>{d.dueDate}</div>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-1.5">
                           <div className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300 flex items-center justify-center text-[8px] font-bold">{d.pm.abbr}</div>
                           <span className="text-xs font-medium text-slate-600 dark:text-slate-400">{d.pm.name}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-center">
                        <button className="text-slate-400 hover:text-blue-500 dark:hover:text-blue-400 p-1" onClick={(e) => { e.stopPropagation(); setSelectedRow(d); }}>
                          <ChevronDown className="w-4 h-4"/>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* 6. Drawer Overlay (Simulated) */}
      <AnimatePresence>
        {selectedRow && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40" onClick={() => setSelectedRow(null)} />
            <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'spring', damping: 25, stiffness: 200 }} 
              className={`fixed top-0 right-0 w-full max-w-[560px] h-full z-50 flex flex-col shadow-2xl border-l border-slate-200/50 dark:border-white/10 ${isDark ? 'bg-slate-900 text-slate-200' : 'bg-white text-slate-900'}`}>
              
              <div className="p-6 pb-0 flex-1 overflow-y-auto">
                <button onClick={() => setSelectedRow(null)} className="absolute top-6 right-6 p-2 rounded-full hover:bg-slate-100 dark:hover:bg-white/5"><X className="w-5 h-5"/></button>
                <div className="mt-4 mb-8">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 dark:bg-white/5 dark:text-slate-400">{selectedRow.code}</span>
                    {selectedRow.unpaid > 0 && <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${selectedRow.bucket === 'd90_plus' ? 'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400'}`}>D+{selectedRow.daysOverdue} 연체중</span>}
                  </div>
                  <h2 className="text-2xl font-bold mb-4">{selectedRow.name}</h2>
                  
                  <div className="grid grid-cols-2 gap-4 p-5 rounded-xl bg-slate-50 border border-slate-100 dark:bg-slate-800/50 dark:border-white/5 mb-6">
                    <div>
                      <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">총 미수금 (남은 잔액)</p>
                      <p className="text-2xl font-mono tabular-nums font-bold text-rose-600 dark:text-rose-400">{selectedRow.unpaid.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">총 계약 금액</p>
                      <p className="text-xl font-mono tabular-nums font-semibold text-slate-700 dark:text-slate-300">{selectedRow.totalAmount.toLocaleString()}</p>
                    </div>
                  </div>

                  {/* Dummy Tabs */}
                  <div className="flex border-b border-slate-200 dark:border-white/10 mb-6">
                    {['개요', '청구·입금 타임라인', '커뮤니케이션 이력', '관련 문서'].map((t, idx) => (
                      <div key={idx} className={`px-4 py-2 text-sm font-semibold cursor-pointer border-b-2 mr-2 ${idx===0 ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400' : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-300'}`}>{t}</div>
                    ))}
                  </div>

                  <div className="space-y-6">
                    <div>
                      <h4 className="text-sm font-bold flex items-center gap-2 mb-3"><User className="w-4 h-4"/> 책임 담당자 정보</h4>
                      <div className="text-sm">발주처: <span className="font-semibold">{selectedRow.vendor.name}</span></div>
                      <div className="text-sm mt-1">사내 PM: <span className="font-semibold">{selectedRow.pm.name}</span> 실장</div>
                      <div className="text-sm mt-1">지급 기일(만기): <span className="font-mono">{selectedRow.dueDate}</span></div>
                    </div>
                    
                    <div className="p-4 rounded-xl border border-blue-100 bg-blue-50/50 dark:border-blue-500/20 dark:bg-blue-500/5">
                      <h4 className="text-xs font-bold text-blue-800 dark:text-blue-300 flex items-center gap-2 mb-2">✨ AI 예측 및 추천</h4>
                      <p className="text-sm text-blue-700 dark:text-blue-400 font-medium leading-relaxed">
                        해당 발주처의 과거 대금 지급 주기를 분석한 결과, 강력한 독촉 메일(2차 경고) 발송 시 **평균 8.4일 이내**에 입금되는 패턴이 있습니다. <br/>
                        예상 입금일: <span className="font-bold underline">2026-05-12</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom CTA */}
              <div className="p-5 border-t border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-[#0f1523] flex gap-2">
                <button className="flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700 flex items-center justify-center gap-2 transition-colors">
                  <Mail className="w-4 h-4"/> 정중한 독촉 생성
                </button>
                <button className="flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white shadow-sm flex items-center justify-center gap-2 transition-colors">
                  <AlertTriangle className="w-4 h-4 text-white/70"/> 등기/법무 발송
                </button>
                <button className="flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm flex items-center justify-center gap-2 transition-colors">
                  <CheckCircle2 className="w-4 h-4"/> 입금 확인
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

    </div>
  );
}

// Watchlist Component Helper
function WatchlistCard({ title, items, icon, type, onProjectClick, onViewAll }: { title: string, items: ProjectAR[], icon: any, type: string, onProjectClick?: (p: any) => void, onViewAll?: () => void }) {
  return (
    <div className="rounded-2xl border border-slate-200/70 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] dark:border-white/[0.06] dark:bg-slate-900/60 p-5 flex flex-col h-full">
      <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">{icon} {title}</h3>
      <div className="flex-1 space-y-1">
        {items.length === 0 ? (
          <div className="text-xs text-slate-500 py-4 italic">해당 항목이 없습니다.</div>
        ) : items.map(d => (
          <div key={d.id} onClick={() => onProjectClick?.(d)} className="flex justify-between items-center py-2 border-b border-transparent hover:border-slate-100 dark:hover:border-white/[0.04] transition-colors cursor-pointer group">
            <div className="min-w-0 pr-2">
              <div className="text-[13px] font-semibold text-slate-800 dark:text-slate-200 truncate group-hover:text-blue-500 transition-colors">{d.name}</div>
              <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{d.vendor.name} · {d.pm.name}</div>
            </div>
            <div className="text-right shrink-0">
              <div className={`text-[13px] font-mono tabular-nums font-bold ${type === 'risk' ? 'text-rose-600 dark:text-rose-400' : 'text-slate-700 dark:text-slate-300'}`}>{formatKoreanAbbr(d.unpaid)}</div>
              <div className={`text-[10px] font-mono mt-0.5 ${type === 'risk' ? 'text-rose-500' : 'text-amber-500'}`}>{type==='risk' ? `D+${d.daysOverdue ?? 0}` : d.dueDate.slice(5)}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 pt-4 border-t border-slate-100 dark:border-white/[0.04] text-xs font-semibold text-blue-600 dark:text-blue-400 cursor-pointer text-center hover:opacity-80" onClick={onViewAll}>전체 보기 →</div>
    </div>
  );
}
